import React, { useState } from "react";
import {
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  InformationCircleIcon,
  LightningBoltIcon,
  BadgeCheckIcon,
  StarIcon,
  UserGroupIcon,
  UserIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";

const KarigarAssignmentModal = ({
  order,
  itemIndex,
  productKarigars,
  otherKarigars,
  recommendations,
  summary,
  loading,
  onClose,
  onAssign,
  onViewQueue,
  formatCurrency,
}) => {
  const [selectedKarigar, setSelectedKarigar] = useState(null);
  const [expectedCompletionDate, setExpectedCompletionDate] = useState(
    order?.expectedDeliveryDate
      ? new Date(order.expectedDeliveryDate).toISOString().split("T")[0]
      : ""
  );
  const [notes, setNotes] = useState("");
  const [assigning, setAssigning] = useState(false);

  const item = order?.items?.[itemIndex];

  const handleAssign = async () => {
    if (!selectedKarigar) return;
    setAssigning(true);
    try {
      await onAssign(selectedKarigar._id || selectedKarigar.karigarId, {
        expectedCompletionDate,
        notes,
        workType: "NEW_MAKING",
      });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-primary to-primary-dark px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Assign Karigar</h3>
            <p className="text-white/80 text-sm">
              {item?.productSnapshot?.name ||
                item?.product?.name ||
                `Item #${itemIndex + 1}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          >
            <XCircleIcon className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-gray-600">
                Loading available karigars...
              </span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              {summary && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">
                      Availability Summary
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-blue-700">Total Available</p>
                      <p className="font-bold text-blue-900">
                        {summary.totalAvailable || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-700">Product Specialists</p>
                      <p className="font-bold text-blue-900">
                        {summary.productKarigarsCount || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-700">Other Capable</p>
                      <p className="font-bold text-blue-900">
                        {summary.otherKarigarsCount || 0}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {recommendations && Object.keys(recommendations).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <LightningBoltIcon className="w-5 h-5 text-amber-500" />
                    Recommended Karigars
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {recommendations.bestOverall && (
                      <button
                        onClick={() =>
                          setSelectedKarigar(recommendations.bestOverall)
                        }
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selectedKarigar?._id ===
                          recommendations.bestOverall._id
                            ? "border-primary bg-primary/5"
                            : "bg-green-50 border-green-300 hover:shadow-md"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <BadgeCheckIcon className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-medium text-green-700">
                            Best Overall
                          </span>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm truncate">
                          {recommendations.bestOverall.name ||
                            recommendations.bestOverall.karigarName}
                        </p>
                        <p className="text-xs text-gray-600">
                          {recommendations.bestOverall.karigarCode}
                        </p>
                      </button>
                    )}
                    {recommendations.fastest && (
                      <button
                        onClick={() =>
                          setSelectedKarigar(recommendations.fastest)
                        }
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selectedKarigar?._id === recommendations.fastest._id
                            ? "border-primary bg-primary/5"
                            : "bg-blue-50 border-blue-300 hover:shadow-md"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <LightningBoltIcon className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">
                            Fastest
                          </span>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm truncate">
                          {recommendations.fastest.name ||
                            recommendations.fastest.karigarName}
                        </p>
                        <p className="text-xs text-gray-600">
                          {recommendations.fastest.karigarCode}
                        </p>
                      </button>
                    )}
                    {recommendations.highestRated && (
                      <button
                        onClick={() =>
                          setSelectedKarigar(recommendations.highestRated)
                        }
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selectedKarigar?._id ===
                          recommendations.highestRated._id
                            ? "border-primary bg-primary/5"
                            : "bg-amber-50 border-amber-300 hover:shadow-md"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <StarIcon className="w-4 h-4 text-amber-600" />
                          <span className="text-xs font-medium text-amber-700">
                            Highest Rated
                          </span>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm truncate">
                          {recommendations.highestRated.name ||
                            recommendations.highestRated.karigarName}
                        </p>
                        <p className="text-xs text-gray-600">
                          {recommendations.highestRated.karigarCode}
                        </p>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Product Karigars */}
              {productKarigars?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <StarIcon className="w-5 h-5 text-amber-500" />
                    Product Specialists ({productKarigars.length})
                  </h4>
                  <div className="space-y-2">
                    {productKarigars.map((karigar) => (
                      <KarigarCard
                        key={karigar._id || karigar.karigarId}
                        karigar={karigar}
                        isSelected={selectedKarigar?._id === karigar._id}
                        onSelect={() => setSelectedKarigar(karigar)}
                        onViewQueue={() =>
                          onViewQueue(karigar._id || karigar.karigarId)
                        }
                        isProductKarigar={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Other Karigars */}
              {otherKarigars?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <UserGroupIcon className="w-5 h-5 text-gray-500" />
                    Other Available Karigars ({otherKarigars.length})
                  </h4>
                  <div className="space-y-2">
                    {otherKarigars.map((karigar) => (
                      <KarigarCard
                        key={karigar._id || karigar.karigarId}
                        karigar={karigar}
                        isSelected={selectedKarigar?._id === karigar._id}
                        onSelect={() => setSelectedKarigar(karigar)}
                        onViewQueue={() =>
                          onViewQueue(karigar._id || karigar.karigarId)
                        }
                        isProductKarigar={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!productKarigars?.length && !otherKarigars?.length && (
                <div className="text-center py-8">
                  <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    No karigars available for this product type
                  </p>
                </div>
              )}

              {/* Assignment Details */}
              {selectedKarigar && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5" />
                    Assignment Details
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Selected Karigar
                      </label>
                      <p className="text-gray-900 font-semibold">
                        {selectedKarigar.name || selectedKarigar.karigarName} (
                        {selectedKarigar.karigarCode})
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expected Completion Date
                      </label>
                      <input
                        type="date"
                        value={expectedCompletionDate}
                        onChange={(e) =>
                          setExpectedCompletionDate(e.target.value)
                        }
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Any special instructions..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={assigning}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!selectedKarigar || assigning}
            className="flex-1"
          >
            {assigning ? (
              <span className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 animate-spin" />
                Assigning...
              </span>
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                Assign Karigar
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Karigar Card Component
const KarigarCard = ({
  karigar,
  isSelected,
  onSelect,
  onViewQueue,
  isProductKarigar,
}) => (
  <div
    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
      isSelected
        ? "border-primary bg-primary/5 shadow-md"
        : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
    }`}
    onClick={onSelect}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isProductKarigar ? "bg-amber-100" : "bg-gray-100"
          }`}
        >
          <UserIcon
            className={`w-5 h-5 ${
              isProductKarigar ? "text-amber-600" : "text-gray-600"
            }`}
          />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-gray-900">
              {karigar.name || karigar.karigarName}
            </p>
            {isProductKarigar && (
              <Badge variant="amber" size="sm">
                Specialist
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-600">
            {karigar.karigarCode} • {karigar.branchCode}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {karigar.rating && (
          <div className="flex items-center gap-1">
            <StarIcon className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium">
              {karigar.rating.toFixed(1)}
            </span>
          </div>
        )}
        {karigar.currentWorkload !== undefined && (
          <Badge
            variant={
              karigar.currentWorkload < 3
                ? "green"
                : karigar.currentWorkload < 5
                ? "yellow"
                : "red"
            }
            size="sm"
          >
            {karigar.currentWorkload} jobs
          </Badge>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewQueue();
          }}
          className="p-1.5 hover:bg-gray-100 rounded-lg"
        >
          <EyeIcon className="w-4 h-4 text-gray-500" />
        </button>
        {isSelected && <CheckCircleIcon className="w-5 h-5 text-primary" />}
      </div>
    </div>
  </div>
);

export default KarigarAssignmentModal;
